/**
 * useConsent — the customer-side DPDP consent gate (S7-7; XC-01 · IFSCA-05 · INV-6).
 *
 * Runs BEFORE the geo gate on the call flow (the geo gate and the media join stay
 * disabled until consent resolves). UNLIKE geo, consent is a GENUINE GATE: a
 * decline cannot proceed to the regulated call — but the customer is told plainly
 * why, and can reconsider. This is the real ceremony the notice-only landing copy
 * was deferring to.
 *
 * Honesty rules encoded here:
 *  • The version + purposes + UNRATIFIED stamp are fetched from the backend and
 *    shown verbatim; the client posts back the SAME version it displayed, so the
 *    server-authoritative cross-check is meaningful (a changed notice → 422,
 *    re-affirm).
 *  • Affirmation is explicit and covers every required purpose — a partial
 *    consent is refused server-side (422) and never silently accepted.
 *  • Mock mode (no backend) still shows the ceremony and lets the customer
 *    affirm, but posts nothing — no evidence rail exists to bind a record to
 *    (data minimisation).
 *  • A 409 (already captured — a reload/rejoin) resolves quietly: the immutable
 *    first affirmation stands and is never re-posted.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/api';
import {
  getConsentText,
  postConsent,
  type ConsentPurposeKey,
  type ConsentText,
} from '@/api/consent';

export type ConsentPhase =
  | 'loading' // fetching the active consent text
  | 'ready' // text shown; awaiting the customer's affirmative action
  | 'posting' // affirmation being recorded
  | 'captured' // recorded — brief confirmation beat
  | 'resolved' // gate finished (captured, already-captured, or mock)
  | 'declined' // customer declined — BLOCKED (honest copy + reconsider)
  | 'error'; // text load / post failed; retry

export interface ConsentGateState {
  readonly phase: ConsentPhase;
  /** True once consent is affirmed (or already on record) — the ONLY way past the gate. */
  readonly resolved: boolean;
  /** The active consent text (version stamp, purposes, notice body) — null until loaded. */
  readonly text: ConsentText | null;
  /** Calm, customer-facing detail line for degrade/decline states (never a stack trace). */
  readonly detail: string | null;
  /** Affirm consent — records a PRE_JOIN record covering the text's required purposes. */
  readonly affirm: () => void;
  /** Decline — blocks the gate with honest copy (the regulated call cannot proceed). */
  readonly decline: () => void;
  /** From declined/error back to the affirmable state. */
  readonly reconsider: () => void;
  /** Retry loading the consent text after a load error. */
  readonly retry: () => void;
}

export function useConsent(sessionId: string, enabled: boolean): ConsentGateState {
  const [phase, setPhase] = useState<ConsentPhase>('loading');
  const [text, setText] = useState<ConsentText | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const disposed = useRef(false);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  const load = useCallback(async () => {
    setPhase('loading');
    setDetail(null);
    try {
      const t = await getConsentText(sessionId);
      if (disposed.current) return;
      setText(t);
      setPhase('ready');
    } catch {
      if (disposed.current) return;
      setDetail('droom.consenthook.loadfail');
      setPhase('error');
    }
  }, [sessionId]);

  // Demo mode / no session: nothing to bind consent to — resolve, ask nothing.
  // Otherwise load the active consent text on entry.
  useEffect(() => {
    if (!enabled) {
      setPhase('resolved');
      return;
    }
    void load();
  }, [enabled, load]);

  const affirm = useCallback(async () => {
    const t = text;
    if (!t) return;
    setPhase('posting');
    setDetail(null);
    try {
      await postConsent(sessionId, {
        consentVersion: t.consentVersion,
        method: 'PRE_JOIN',
        acceptedPurposes: t.purposes as ConsentPurposeKey[],
      });
      if (disposed.current) return;
      setPhase('captured');
      window.setTimeout(() => {
        if (!disposed.current) setPhase('resolved');
      }, 700); // brief confirmation beat, then the geo gate / call
    } catch (e) {
      if (disposed.current) return;
      if (e instanceof ApiError && e.status === 409) {
        // Already captured (a reload/rejoin) — the immutable first affirmation stands.
        setPhase('resolved');
        return;
      }
      if (e instanceof ApiError && e.status === 422) {
        // The notice changed under the customer (stale version) — re-fetch + re-affirm.
        setDetail('droom.consenthook.updated');
        void load();
        return;
      }
      setDetail('droom.consenthook.recordfail');
      setPhase('error');
    }
  }, [sessionId, text, load]);

  const decline = useCallback(() => {
    setDetail(null);
    setPhase('declined');
  }, []);

  const reconsider = useCallback(() => {
    setDetail(null);
    setPhase(text ? 'ready' : 'loading');
    if (!text) void load();
  }, [text, load]);

  return {
    phase,
    resolved: phase === 'resolved',
    text,
    detail,
    affirm: () => void affirm(),
    decline,
    reconsider,
    retry: () => void load(),
  };
}
